import type { Territory } from "$global-providers/config-provider/provider";
import { useConfig } from "$global-providers/config-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import * as LDClient from "launchdarkly-js-client-sdk";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLazyUser } from "../user-provider";

const FEATURE_FLAGS_DISABLED_UNTIL_PNE_655_REPLACES_INVOICE_CONFLICT_HANDLING =
  new Set([featureFlags.billing.rxdbBilling]);

export const FeatureFlagContext = createContext<{
  flags: FeatureFlags;
  loading: boolean;
  updateFlags: (newFlags: { [flag: string]: FeatureFlagAllowedTypes }) => void;
}>({
  loading: true,
  flags: {},
  updateFlags: () => {},
});

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagProvider",
    );
  }
  return context;
};

/**
 * LD does provide a React SDK, however it doesn't look like it supports being able to update flags locally,
 * which we make good use of in the DevTools. In light of this, we using the JS SDK directly.
 *
 * Unused React SDK: https://launchdarkly.github.io/react-client-sdk/index.html
 */
const CustomLDFFProvider: FCwC<{
  territory: Territory;
  launchDarkly: {
    key: string;
    relayUrl?: string;
  };
  user?: {
    id: string;
    email: string;
    facilityId: string;
    organisationId: string;
  };
}> = ({ territory, user, launchDarkly, children }) => {
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState(true);
  const initialised = useRef(false);

  const [e2eFlags, setE2eFlags] = useState<FeatureFlags | undefined>(undefined);
  useEffect(() => {
    if (initialised.current || !user) {
      return;
    }
    // Any e2e.* or e2etesting* user gets the localStorage flag override path.
    // The previous narrower regex (e2e.user|e2etesting) silently no-op'd for
    // role-based e2e logins like e2e.doctor@elephant.healthcare, which masked
    // the lack of per-spec flag coverage in CI.
    if (user.email.match(/(e2e\.\w+|e2etesting).*@elephant\.healthcare/g)) {
      const localStorageFlags = localStorage.getItem("ld:e2eFlags");
      const localFlags = localStorageFlags
        ? JSON.parse(localStorageFlags)
        : undefined;
      setE2eFlags(localFlags);
      setLoading(false);

      // TODO: get rid of this command in favour of approach just above
      // biome-ignore lint/suspicious/noExplicitAny: Hack for Cypress E2E tests
      (window as any).e2eSetLaunchDarklyFlags = (e2eFlags: FeatureFlags) => {
        setFlags((existingFlags) => ({ ...existingFlags, ...e2eFlags }));
        setLoading(false);
      };
      return;
    }

    const client = LDClient.initialize(
      launchDarkly.key,
      {
        key: user.id,
        email: user.email,
        custom: {
          facilityId: user.facilityId,
          organisationId: user.organisationId,
          territory,
          // @deprecated - to remove once we've migrated all LD config to use `facilityId`
          clinicId: user.facilityId,
          // We must specify it in the user context too, as LD UI doesn't currently allow you to use the application.version
          // rules for the web SDK, only the mobile SDKs. This allows us to enable flags for only users on a certain version,
          // which is critical for enabling a feature and ensuring that user's on older versions don't see it and see a broken
          // mid-development feature.
          appVersionSemantic: APP_VERSION_SEMANTIC,
        },
      },
      {
        application: {
          id: SERVICE_NAME,
          version: APP_VERSION_SEMANTIC,
        },
        useReport: true, // Must be true to avoid exposing PII via a url param: https://trello.com/c/CJgueMjj
        // We need to persist the flags since we need them to be available offline.
        // The latest versions are fetched while online so they will stay up to date.
        // https://docs.launchdarkly.com/sdk/features/bootstrapping#bootstrapping-using-local-storage
        bootstrap: "localStorage",
      },
    );
    client.on("ready", () => {
      setFlags((currFlags) => ({ ...currFlags, ...client.allFlags() }));
      setLoading(false);
    });

    client.on("change", (newFlags) => {
      setFlags((currFlags) => ({
        ...currFlags,
        ...getCurrentFlagValues(newFlags),
      }));
    });

    initialised.current = true;
  }, [
    territory,
    launchDarkly.key,
    user,
    user?.id,
    user?.email,
    user?.facilityId,
    user?.organisationId,
  ]);

  return (
    <FeatureFlagContext.Provider
      value={{
        loading,
        flags: e2eFlags ?? flags,
        updateFlags: (newFlags: FeatureFlags) =>
          setFlags((currFlags) => ({ ...currFlags, ...newFlags })),
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const FeatureFlagsProvider: FCwC = ({ children }) => {
  const config = useConfig();
  const user = useLazyUser();

  return (
    <CustomLDFFProvider
      territory={config.territory}
      launchDarkly={{
        key: config.launchdarkly.key,
        relayUrl: config.launchdarkly.relayUrl,
      }}
      user={
        user
          ? {
              id: user.id,
              email: user.email,
              facilityId: user.facilityId,
              organisationId: user.organisationId,
            }
          : undefined
      }
    >
      {children}
    </CustomLDFFProvider>
  );
};

export type FeatureFlags = Record<string, FeatureFlagAllowedTypes>;

const getCurrentFlagValues = (
  launchDarklyFlags: LDClient.LDFlagSet,
): FeatureFlags =>
  Object.keys(launchDarklyFlags).reduce((acc, key) => {
    const { current } = launchDarklyFlags[key];
    acc[key] = current;
    return acc;
  }, {});
export type FeatureFlagAllowedTypes = boolean | number | string;

export function useFeatureFlag<T extends FeatureFlagAllowedTypes = boolean>(
  flagKey: string,
): [T, { loading: boolean }] {
  const { flags, loading } = useFeatureFlagContext();
  if (
    FEATURE_FLAGS_DISABLED_UNTIL_PNE_655_REPLACES_INVOICE_CONFLICT_HANDLING.has(
      flagKey,
    )
  ) {
    return [false as T, { loading: false }];
  }
  return [flags[flagKey] as T, { loading }];
}
